<template>
    <div>
        <div class="select-time-container">
            <div v-if="disabled" class="fixed-time-container">
                <date-picker v-model="timeValue" :disabled="true" @change="selectTime" format="HH:mm"
                    value-type="format" type="time" placeholder="HH:mm"></date-picker>
            </div>
            <div v-if="!disabled" class="manual-time-container">
                <date-picker v-model="timeValue" @change="selectTime" format="HH:mm" value-type="format" type="time"
                    placeholder="HH:mm"></date-picker>
                <span v-if="slotWarning || outsideOpeningHours || !hasSyncBookingSystem" class="warning-header">Warning(s)</span>
                <span v-if="outsideOpeningHours" class="warning">{{ outsideOpeningHoursText }}</span>
                <span v-else-if="slotWarning" class="warning">Picking a start time outside of a timeslot might
                    result in
                    non-optimal scheduling.
                </span>
                <span v-if="!hasSyncBookingSystem" class="warning">
                    Since this location does not use a synchronized booking system,
                    moving the booking needs to be done manually in the locations booking system.
                </span>
            </div>

        </div>
        <div v-if="!disabled" class="time-slots-container">
            <div v-for="{ time, status, period } in timeSlotsWithPeriods" :key="time"
                :class="[status, periodsOverlap(period[0], period[1], playPeriod[0], playPeriod[1]) ? 'occupied' : '']"
                class="time-slot" @click="selectTime(time)">
                {{ time }}
            </div>
        </div>
    </div>
</template>

<script setup>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { watch, ref, computed } from 'vue';
import dayjs from "dayjs";
import { periodsOverlap } from '../../helpers/dates';

const props = defineProps({
    timeSlots: {
        type: Array,
        required: true,
    },
    value: {
        type: String,
        required: true,
    },
    duration: {
        type: Number,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    hasSyncBookingSystem: {
        type: Boolean,
        default: true,
    },
    outsideOpeningHours: {
        type: Boolean,
        default: false,
    },
    dayOutsideOpeningHours: {
        type: Boolean,
        default: false,
    },
    outsideOpeningHoursText: {
        type: String,
        default: "This time is outside the location's opening hours. You can still book it.",
    },
});

const playPeriod = computed(() => {
    if (!props.value) {
        return [null, null];
    }
    const [hours, minutes] = props.value.split(':').map(Number);
    const startTime = dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
    const endTime = startTime.add(props.duration, 'minutes');
    return [startTime, endTime];
});

const timeSlotsWithPeriods = computed(() => {
    return props.timeSlots.map((slot, index) => {
        const [hours, minutes] = slot.time.split(':').map(Number);
        const startTime = dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
        let endTime = startTime.add(15, 'minutes');
        if (props.timeSlots.length > index + 1) {
            const [hours, minutes] = props.timeSlots[index + 1].time.split(':').map(Number);
            endTime = dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
        }
        return {
            ...slot,
            period: [startTime, endTime]
        };
    });
});

const showWarning = computed(() => {
    if (!props.value) {
        return false;
    }
    return !props.timeSlots.some(({ time }) => time === props.value)
});

const slotWarning = computed(() => showWarning.value && !props.dayOutsideOpeningHours);

const timeValue = ref(props.value);

const emit = defineEmits(["input"]);

function selectTime(time) {
    emit("input", time);
}

watch(() => props.value, (newValue) => {
    timeValue.value = newValue;
});


</script>

<style lang="scss" scoped>
@use "../../colors" as *;

.select-time-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.fixed-time-container {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: left;
    align-items: center;
}

.manual-time-container {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: left;
    align-items: center;

    .warning-header {
        color: #ff0;
        font-size: 1rem;
        font-weight: bold;
        align-self: center;
    }

    .warning {
        color: #ff0;
        font-size: 0.875rem;
        line-height: 1rem;
    }
}

@media (max-width: 640px) {
    .manual-time-container {
        flex-direction: column;
        align-items: flex-start;
    }
}

.time-slots-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1rem;

    .time-slot {
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: not-allowed;
        user-select: none;

        &.past {
            background-color: gray;
        }

        &.unavailable {
            background-color: red;
        }


        &.available {
            cursor: pointer;
            user-select: auto;
            background-color: green;

            &.occupied {
                background-color: indigo;
            }

            &:hover {
                background-color: lightgreen;
            }
        }

        &.selected {
            background-color: blue;
            color: white;
        }
    }
}
</style>